/* eslint-disable camelcase */
/* eslint-disable max-lines */
import { Team } from '@litlingo/client';
import {
  AntitrustAssessmentExportPayload,
  exportAntitrustAssessment,
  showErrorAlert,
} from 'actions';
import { useCasesOptions } from 'components/CustomerImport/EditIntegrationSettings/IntegrationSettingsUseCases';
import DateFilterGeneral from 'components/Filters/SidebarTypeFilters/DateFilterGeneral';
import LinkLookup from 'components/LinkLookup';
import MultiselectDropdownRedesign, {
  MultiSelectOption,
} from 'components/MultiSelectDropdown/MultiSelectDropdownRedesign';
import Permissions from 'components/Permissions';
import Labels from 'components/UsersPermissions/UserPermissionsForm/Labels';
import useAllTeams from 'hooks/teams/useAllTeams';
import moment from 'moment';
import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getAntitrustAssessmentExportLoading } from 'selectors/analytics';
import { getCustomerName } from 'selectors/auth';
import { getTags } from 'selectors/tags';

type DateRangeState = {
  createdAfter: string;
  createdBefore: string;
};

const addTeams = (current: Team[], incoming: Team[]): Team[] => {
  const existing = new Set(current.map((team) => team.uuid));
  return [...current, ...incoming.filter((team) => !existing.has(team.uuid))];
};

const removeTeams = (current: Team[], outgoing: Team[]): Team[] => {
  const toRemove = new Set(outgoing.map((team) => team.uuid));
  return current.filter((team) => !toRemove.has(team.uuid));
};

const AntitrustAssessmentForm: React.FC = () => {
  const dispatch = useDispatch();
  const loading = useSelector(getAntitrustAssessmentExportLoading);
  const tags = useSelector(getTags);
  const customerName = useSelector(getCustomerName);

  const { data: regionsData } = useAllTeams({ kind: 'region', redirectOnError: false });

  const [dateRange, setDateRange] = useState<DateRangeState>({
    createdAfter: '',
    createdBefore: '',
  });
  const [selectedUseCases, setSelectedUseCases] = useState<MultiSelectOption[]>([]);
  const [selectedRegions, setSelectedRegions] = useState<Team[]>([]);
  const [includeSubRegions, setIncludeSubRegions] = useState(false);
  const [selectedTags, setSelectedTags] = useState<MultiSelectOption[]>([]);

  const tagOptions = useMemo(
    () =>
      tags.map((tag) => ({
        label: tag.value,
        value: tag.uuid,
      })),
    [tags]
  );

  const regionOptions = useMemo(
    () =>
      (regionsData?.records || []).map((region: Team) => ({
        label: region.name,
        value: region.uuid,
      })),
    [regionsData]
  );

  const handleSetDate = useCallback((next: DateRangeState): void => {
    setDateRange(next);
  }, []);

  const handleSingleSelectChange = useCallback(
    (
      setter: React.Dispatch<React.SetStateAction<MultiSelectOption[]>>,
      option: MultiSelectOption
    ): void => {
      setter((prev) => (prev.some((item) => item.value === option.value) ? [] : [option]));
    },
    []
  );

  const handleRegionChange = (option: MultiSelectOption): void => {
    const existing = selectedRegions.find((region) => region.uuid === option.value);
    if (existing) {
      setSelectedRegions((prev) => removeTeams(prev, [existing]));
      return;
    }
    const region = (regionsData?.records || []).find((item) => item.uuid === option.value);
    if (region) {
      setSelectedRegions((prev) => addTeams(prev, [region]));
    }
  };

  const handleRegionDelete = (option: MultiSelectOption): void => {
    const region = selectedRegions.find((item) => item.uuid === option.value);
    if (region) {
      setSelectedRegions((prev) => removeTeams(prev, [region]));
    }
  };

  const buildRegionsPayload = (): AntitrustAssessmentExportPayload['regions'] => {
    if (!selectedRegions.length) {
      return [];
    }

    const named = selectedRegions.map((region) => ({
      name: region.name,
      uuid: region.uuid,
    }));

    if (includeSubRegions) {
      return [
        {
          name: named.map((region) => region.name).join(' / '),
          sub_regions: named,
        },
      ];
    }

    return named.map((region) => ({ ...region, sub_regions: [] }));
  };

  const buildPayload = (): AntitrustAssessmentExportPayload | null => {
    const start = moment(dateRange.createdAfter);
    const end = moment(dateRange.createdBefore);
    if (!dateRange.createdAfter || !dateRange.createdBefore || !start.isValid() || !end.isValid()) {
      dispatch(showErrorAlert('Date Range is required'));
      return null;
    }
    if (start.isAfter(end)) {
      dispatch(showErrorAlert('Start date must be before end date'));
      return null;
    }
    const useCaseUuid = selectedUseCases[0]?.value;
    if (!useCaseUuid) {
      dispatch(showErrorAlert('Use Case is required'));
      return null;
    }

    const regions = buildRegionsPayload();
    const regionUuids = selectedRegions.map((region) => region.uuid);
    const tagUuid = selectedTags[0]?.value;

    return {
      start_time: Math.floor(start.valueOf() / 1000),
      end_time: Math.floor(end.valueOf() / 1000),
      higher_priority_campaign_uuids: [useCaseUuid],
      regions,
      region_uuids: regionUuids,
      email_hygiene_tag_uuids: tagUuid ? [tagUuid] : [],
      include_associated_sub_regions: includeSubRegions,
      file_title: [
        (customerName || 'customer').replace(/\s+/g, '_'),
        `${start.format('MMDDYYYY')}-${end.format('MMDDYYYY')}`,
        selectedRegions.length ? selectedRegions.map((region) => region.name).join('-') : 'all',
      ]
        .join('_')
        .replace(/[^\w.-]+/g, '_'),
    };
  };

  const handleGenerate = (): void => {
    const payload = buildPayload();
    if (!payload) return;
    dispatch(exportAntitrustAssessment(payload));
  };

  return (
    <div className="relative pt-16 pl-10 pr-10 pb-10">
      <div className="absolute top-[62px] right-10 flex flex-row gap-3">
        <LinkLookup
          routeName="customer-overview"
          className="button button--secondary h-10 px-6 font-bold justify-center"
          data-testid="antitrust-assessment-cancel"
        >
          Cancel
        </LinkLookup>
        <Permissions action="analytics.list">
          <button
            type="button"
            className="button button--primary h-10 px-6 font-bold justify-center"
            disabled={loading}
            onClick={handleGenerate}
            data-testid="antitrust-assessment-export"
          >
            Generate
          </button>
        </Permissions>
      </div>

      <h1 className="text-heading-1">Generate Assessment</h1>

      <div className="flex flex-col mt-6">
        <div className={`flex flex-col gap-2 w-64 `}>
          <span>
            Date Range <span className="text-litlingo-alert">*</span>
          </span>
          <DateFilterGeneral
            showRangeOptions={false}
            dateRange={dateRange}
            handleSetDate={handleSetDate}
          />
        </div>

        <div className="flex flex-col gap-2 mt-4">
          <span>
            Use Case <span className="text-litlingo-alert">*</span>
          </span>
          <div className="flex flex-row gap-2 items-start">
            <div className="w-78">
              <MultiselectDropdownRedesign
                className="w-full"
                placeholder="Select"
                options={useCasesOptions}
                selectedItems={selectedUseCases}
                onChange={(option): void => handleSingleSelectChange(setSelectedUseCases, option)}
                enableSearch
                dataTestid="antitrust-assessment-use-case"
              />
            </div>
            <Labels
              includedLabels={selectedUseCases}
              excludedLabels={[]}
              handleDelete={(option): void => handleSingleSelectChange(setSelectedUseCases, option)}
            />
          </div>
        </div>

        <div className="flex flex-col gap-2 mt-6">
          <span>Region(s)</span>
          <div className="flex flex-row gap-2 items-start">
            <div className="w-78">
              <MultiselectDropdownRedesign
                className="w-full"
                placeholder="Select region"
                options={regionOptions}
                selectedItems={selectedRegions.map((region) => ({
                  label: region.name,
                  value: region.uuid,
                }))}
                onChange={handleRegionChange}
                enableSearch
                dataTestid="antitrust-assessment-regions"
              />
            </div>
            <Labels
              includedLabels={selectedRegions.map((region) => ({
                label: region.name,
                value: region.uuid,
              }))}
              excludedLabels={[]}
              handleDelete={handleRegionDelete}
            />
          </div>
          <label
            htmlFor="include-sub-regions"
            className="inline-flex flex-row gap-2 items-center text-small mt-2"
          >
            <input
              id="include-sub-regions"
              type="checkbox"
              className="form-checkbox litlingo-checkbox h-4 w-4 transition duration-150 ease-in-out"
              checked={includeSubRegions}
              onChange={(e): void => setIncludeSubRegions(e.target.checked)}
            />
            Include associated sub-region(s)
          </label>
        </div>

        <div className="flex flex-col gap-2 mt-4">
          <span>Tag</span>
          <p className="text-small text-litlingo-gray-5">
            A customer tag that specifies their own escalated tracking
          </p>
          <div className="flex flex-row gap-2 items-start">
            <div className="w-78">
              <MultiselectDropdownRedesign
                className="w-full"
                placeholder="Select"
                options={tagOptions}
                selectedItems={selectedTags}
                onChange={(option): void => handleSingleSelectChange(setSelectedTags, option)}
                enableSearch
                dataTestid="antitrust-assessment-tag"
              />
            </div>
            <Labels
              includedLabels={selectedTags}
              excludedLabels={[]}
              handleDelete={(option): void => handleSingleSelectChange(setSelectedTags, option)}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AntitrustAssessmentForm;
